import React, { useRef, useState } from "react";

export const IMPR_COLOR = "#323084";
export const CONV_COLOR = "#22c55e";

export function AreaChart({ width = 820, height = 200, impressionsByDay = [], conversionsByDay = [], dateLabels = [], showImpressions = true, showConversions = true }) {
  const impressData = impressionsByDay.length ? impressionsByDay : [0];
  const convData = conversionsByDay.length ? conversionsByDay : [0];
  const days = Math.max(impressData.length, convData.length, 1);

  const pad = { t:20, r:52, b:36, l:58 };
  const W = width - pad.l - pad.r;
  const H = height - pad.t - pad.b;

  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null); // { i, left, top }

  const normArr = (arr) => { const mx = Math.max(...arr) || 1; return arr.map(v => v / mx); };

  const pts = (arr) => {
    const a = arr.length >= days ? arr : [...arr, ...Array(days - arr.length).fill(0)];
    return normArr(a).map((v, i) => [
      pad.l + (i / Math.max(days - 1, 1)) * W,
      pad.t + H - v * H,
    ]);
  };

  const pathD = (points, yMin, yMax) => {
    if (points.length < 2) return `M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`;
    let d = `M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`;
    const clampY = (y) => Math.min(Math.max(y, yMin), yMax);
    for (let i = 0; i < points.length - 1; i++) {
      const x0 = i > 0 ? points[i - 1][0] : points[0][0];
      const y0 = i > 0 ? points[i - 1][1] : points[0][1];
      const x1 = points[i][0];
      const y1 = points[i][1];
      const x2 = points[i + 1][0];
      const y2 = points[i + 1][1];
      const x3 = i < points.length - 2 ? points[i + 2][0] : x2;
      const y3 = i < points.length - 2 ? points[i + 2][1] : y2;
      const cp1x = x1 + (x2 - x0) / 6;
      const cp1y = clampY(y1 + (y2 - y0) / 6);
      const cp2x = x2 - (x3 - x1) / 6;
      const cp2y = clampY(y2 - (y3 - y1) / 6);
      d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)}`;
    }
    return d;
  };

  const impressPts = pts(impressData);
  const convPts = pts(convData);
  const baseline = pad.t + H;

  const impressMax = Math.max(...impressData, 1);
  const convMax = Math.max(...convData, 1);
  const impressTicks = [0, Math.ceil(impressMax * 0.33), Math.ceil(impressMax * 0.66), impressMax].filter((v, i, a) => !i || v !== a[i - 1]);
  const convTicks = [0, Math.ceil(convMax * 0.33), Math.ceil(convMax * 0.66), convMax].filter((v, i, a) => !i || v !== a[i - 1]);

  const fmt = (v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : `${v}`;

  const xLabels = dateLabels.length >= days ? dateLabels : Array.from({ length: days }, (_, i) => `Day ${i + 1}`);

  const maxXLabels = 8;
  const xLabelIndices = days <= maxXLabels
    ? Array.from({ length: days }, (_, i) => i)
    : Array.from({ length: maxXLabels }, (_, i) =>
        i === maxXLabels - 1 ? days - 1 : Math.round((i / (maxXLabels - 1)) * (days - 1))
      )
      .filter((idx, i, arr) => i === 0 || idx !== arr[i - 1])
      .sort((a, b) => a - b);

  const handleMouseMove = (e) => {
    if (!svgRef.current || days <= 1) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scale = width / rect.width;
    const viewboxX = (e.clientX - rect.left) * scale;
    const rawIndex = (viewboxX - pad.l) / W * (days - 1);
    const i = Math.max(0, Math.min(days - 1, Math.round(rawIndex)));
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const tooltipW = 152;
    const left = mouseX + 14 + tooltipW > rect.width ? mouseX - tooltipW - 8 : mouseX + 14;
    setTooltip({ i, left, top: Math.max(4, mouseY - 44) });
  };

  const crosshairX = tooltip != null
    ? pad.l + (tooltip.i / Math.max(days - 1, 1)) * W
    : null;

  return (
    <div style={{ position: "relative" }} onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow:"hidden", display:"block" }}>
        <defs>
          <linearGradient id="impressGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={IMPR_COLOR} stopOpacity="0.15" />
            <stop offset="100%" stopColor={IMPR_COLOR} stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="convGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CONV_COLOR} stopOpacity="0.12" />
            <stop offset="100%" stopColor={CONV_COLOR} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Gridlines */}
        {impressTicks.map((t, i) => {
          const y = pad.t + H - (t / impressMax) * H;
          return <line key={i} x1={pad.l} y1={y} x2={pad.l + W} y2={y} stroke="#E8E8E8" strokeWidth="1" />;
        })}

        {/* Crosshair */}
        {crosshairX != null && (
          <line x1={crosshairX} y1={pad.t} x2={crosshairX} y2={pad.t + H} stroke="#CFCFCF" strokeWidth="1" strokeDasharray="4 3" />
        )}

        {/* Left Y-axis labels (Impressions) */}
        {showImpressions && impressTicks.map((t, i) => {
          const y = pad.t + H - (t / impressMax) * H;
          return <text key={i} x={pad.l - 8} y={y + 4} fontSize="10" fill={IMPR_COLOR} textAnchor="end" fontWeight="500">{fmt(t)}</text>;
        })}

        {/* Right Y-axis labels (Conversions) */}
        {showConversions && convTicks.map((t, i) => {
          const y = pad.t + H - (t / convMax) * H;
          return <text key={i} x={pad.l + W + 8} y={y + 4} fontSize="10" fill={CONV_COLOR} textAnchor="start" fontWeight="500">{t}</text>;
        })}

        {/* Left axis label */}
        {showImpressions && (
          <text x={10} y={pad.t + H / 2} fontSize="9" fill={IMPR_COLOR} textAnchor="middle" fontWeight="500"
            transform={`rotate(-90, 10, ${pad.t + H / 2})`}>Impressions</text>
        )}

        {/* Right axis label */}
        {showConversions && (
          <text x={width - 10} y={pad.t + H / 2} fontSize="9" fill={CONV_COLOR} textAnchor="middle" fontWeight="500"
            transform={`rotate(90, ${width - 10}, ${pad.t + H / 2})`}>Conversions</text>
        )}

        {/* Impressions line */}
        {showImpressions && (
          <>
            <path d={pathD(impressPts, pad.t, baseline)} fill="none" stroke={IMPR_COLOR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={impressPts[days - 1][0]} cy={impressPts[days - 1][1]} r="4" fill={IMPR_COLOR} />
          </>
        )}

        {/* Conversions line */}
        {showConversions && (
          <>
            <path d={pathD(convPts, pad.t, baseline)} fill="none" stroke={CONV_COLOR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={convPts[days - 1][0]} cy={convPts[days - 1][1]} r="4" fill={CONV_COLOR} />
          </>
        )}

        {/* Hover dots */}
        {tooltip != null && (
          <>
            {showImpressions && <circle cx={impressPts[tooltip.i][0]} cy={impressPts[tooltip.i][1]} r="4" fill="#fff" stroke={IMPR_COLOR} strokeWidth="2" />}
            {showConversions && <circle cx={convPts[tooltip.i][0]} cy={convPts[tooltip.i][1]} r="4" fill="#fff" stroke={CONV_COLOR} strokeWidth="2" />}
          </>
        )}

        {/* X-axis labels */}
        {xLabelIndices.map((i) => {
          const x = pad.l + (i / Math.max(days - 1, 1)) * W;
          const label = xLabels[i] ?? "";
          return <text key={i} x={x} y={height - 4} fontSize="10" fill="#7b7b7b" textAnchor="middle">{label}</text>;
        })}
      </svg>

      {tooltip != null && (() => {
        const { i, left, top } = tooltip;
        const imprVal = impressData[i] ?? 0;
        const convVal = convData[i] ?? 0;
        const label = xLabels[i] ?? "";
        return (
          <div style={{
            position: "absolute",
            left,
            top,
            background: "#FFFFFF",
            border: "0.5px solid #E2E2E2",
            borderRadius: 8,
            padding: "8px 12px",
            pointerEvents: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            fontSize: 12,
            zIndex: 10,
            minWidth: 152,
          }}>
            <div style={{ color: "#626262", marginBottom: 6, fontWeight: 600, fontSize: 11, letterSpacing: 0.3 }}>{label}</div>
            {showImpressions && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: showConversions ? 4 : 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: IMPR_COLOR, display: "inline-block", flexShrink: 0 }} />
                <span style={{ color: "#626262" }}>Impressions</span>
                <span style={{ fontWeight: 600, color: "#151515", marginLeft: "auto" }}>{fmt(imprVal)}</span>
              </div>
            )}
            {showConversions && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: CONV_COLOR, display: "inline-block", flexShrink: 0 }} />
                <span style={{ color: "#626262" }}>Conversions</span>
                <span style={{ fontWeight: 600, color: "#151515", marginLeft: "auto" }}>{convVal.toLocaleString()}</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
